// components/ProductSelection/FilterSidebar.jsx
import React, { useState, useEffect, useRef } from "react";
import Dropdown from "../../common/Dropdown";
import Button from "../../common/Button";
import {
  PRODUCT_TYPE_ENUM,
  PRODUCT_SUBTYPE_ENUM,
  PURITY_ENUM,
  METAL_COLOR_ENUM,
} from "./utils/constants";
import {
  FilterIcon,
  XIcon,
  RefreshIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/outline";

const FilterSidebar = ({
  filters,
  onFilterChange,
  categories,
  onApply,
  onReset,
  resultCount = 0,
  autoApply = true, // New prop
  debounceDelay = 500, // Delay for text inputs
}) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [expandedSections, setExpandedSections] = useState({
    type: true,
    weight: false,
    metal: false,
    price: false,
    attributes: false,
  });

  // Debounce timers for text inputs
  const debounceTimers = useRef({
    minGrossWeight: null,
    maxGrossWeight: null,
    minNetWeight: null,
    maxNetWeight: null,
    minPrice: null,
    maxPrice: null,
  });

  // Sync local filters with parent when filters prop changes
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleChange = (field, value, shouldDebounce = false) => {
    const newFilters = {
      ...localFilters,
      [field]: value,
    };
    setLocalFilters(newFilters);

    // Always update parent state immediately
    onFilterChange(newFilters);

    if (autoApply) {
      if (shouldDebounce) {
        // Clear existing timer for this field
        if (debounceTimers.current[field]) {
          clearTimeout(debounceTimers.current[field]);
        }

        // Set new timer - pass the new filters to onApply
        debounceTimers.current[field] = setTimeout(() => {
          if (onApply) {
            console.log("Debounced apply with filters:", newFilters); // Debug
            onApply(newFilters); // ✅ FIXED: Pass new filters
          }
        }, debounceDelay);
      } else {
        // Apply immediately for dropdowns and selects
        if (onApply) {
          console.log("Immediate apply with filters:", newFilters); // Debug
          onApply(newFilters); // ✅ FIXED: Pass new filters
        }
      }
    }
  };

  const handleReset = () => {
    // Clear all debounce timers
    Object.values(debounceTimers.current).forEach((timer) => {
      if (timer) clearTimeout(timer);
    });
    onReset();
  };

  const categoryOptions = categories.map((cat) => ({
    _id: cat._id || cat.id,
    name: cat.name,
  }));

  const selectedCategories = categoryOptions.filter((opt) =>
    localFilters.categories?.includes(opt._id)
  );

  const renderSection = (key, title, children) => {
    const isExpanded = expandedSections[key];
    return (
      <div className="border-b border-gray-200 last:border-b-0">
        <button
          onClick={() => toggleSection(key)}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <span className="font-medium text-gray-900">{title}</span>
          {isExpanded ? (
            <ChevronUpIcon className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDownIcon className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {isExpanded && <div className="px-4 pb-4 space-y-3">{children}</div>}
      </div>
    );
  };

  return (
    <div className="w-80 bg-white border-r border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FilterIcon className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold text-gray-900">Filters</h3>
            {autoApply && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                Auto
              </span>
            )}
          </div>
          <button
            onClick={handleReset}
            className="text-sm text-gray-600 hover:text-primary flex items-center gap-1 transition-colors"
          >
            <RefreshIcon className="w-4 h-4" />
            Reset
          </button>
        </div>
        {resultCount > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {resultCount} product{resultCount !== 1 ? "s" : ""} found
            </p>
            {autoApply && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-gray-500">Live</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex-1 overflow-y-auto">
        {/* Type & Category */}
        {renderSection(
          "type",
          "Type & Category",
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Type
              </label>
              <select
                value={localFilters.type}
                onChange={(e) => handleChange("type", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-all"
              >
                <option value="">All Types</option>
                {PRODUCT_TYPE_ENUM.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sub Type
              </label>
              <select
                value={localFilters.subType}
                onChange={(e) => handleChange("subType", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-all"
              >
                <option value="">All Sub Types</option>
                {PRODUCT_SUBTYPE_ENUM.map((subType) => (
                  <option key={subType} value={subType}>
                    {subType.replace("_", " ")}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Dropdown
                label="Categories"
                options={categoryOptions}
                selected={selectedCategories}
                onChange={(selected) => {
                  const categoryIds = selected
                    ? selected.map((item) => item._id)
                    : [];
                  handleChange("categories", categoryIds);
                }}
                multiple={true}
                placeholder="Select categories"
                clearable={true}
              />
            </div>
          </>
        )}

        {/* Weight Range */}
        {renderSection(
          "weight",
          "Weight Range",
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Min Gross (g)
                </label>
                <input
                  type="number"
                  value={localFilters.minGrossWeight}
                  onChange={(e) =>
                    handleChange("minGrossWeight", e.target.value, true)
                  }
                  min="0"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-all"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Max Gross (g)
                </label>
                <input
                  type="number"
                  value={localFilters.maxGrossWeight}
                  onChange={(e) =>
                    handleChange("maxGrossWeight", e.target.value, true)
                  }
                  min="0"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-all"
                  placeholder="100"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Min Net (g)
                </label>
                <input
                  type="number"
                  value={localFilters.minNetWeight}
                  onChange={(e) =>
                    handleChange("minNetWeight", e.target.value, true)
                  }
                  min="0"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-all"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Max Net (g)
                </label>
                <input
                  type="number"
                  value={localFilters.maxNetWeight}
                  onChange={(e) =>
                    handleChange("maxNetWeight", e.target.value, true)
                  }
                  min="0"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-all"
                  placeholder="100"
                />
              </div>
            </div>
          </>
        )}

        {/* Metal Details */}
        {renderSection(
          "metal",
          "Metal Details",
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Purity
              </label>
              <select
                value={localFilters.purity}
                onChange={(e) => handleChange("purity", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-all"
              >
                <option value="">All Purity</option>
                {PURITY_ENUM.map((purity) => (
                  <option key={purity} value={purity}>
                    {purity}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Metal Color
              </label>
              <select
                value={localFilters.metalColor}
                onChange={(e) => handleChange("metalColor", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-all"
              >
                <option value="">All Colors</option>
                {METAL_COLOR_ENUM.map((color) => (
                  <option key={color} value={color}>
                    {color.replace("_", " ")}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {/* Price Range */}
        {renderSection(
          "price",
          "Price Range (₹)",
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Min Price
                </label>
                <input
                  type="number"
                  value={localFilters.minPrice}
                  onChange={(e) =>
                    handleChange("minPrice", e.target.value, true)
                  }
                  min="0"
                  step="1000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-all"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Max Price
                </label>
                <input
                  type="number"
                  value={localFilters.maxPrice}
                  onChange={(e) =>
                    handleChange("maxPrice", e.target.value, true)
                  }
                  min="0"
                  step="1000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-all"
                  placeholder="1000000"
                />
              </div>
            </div>
          </>
        )}

        {/* Attributes */}
        {renderSection(
          "attributes",
          "Attributes",
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hallmark Status
              </label>
              <select
                value={localFilters.isHallmarked}
                onChange={(e) => handleChange("isHallmarked", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-all"
              >
                <option value="">All</option>
                <option value="true">Hallmarked Only</option>
                <option value="false">Non-Hallmarked</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Featured
              </label>
              <select
                value={localFilters.isFeatured}
                onChange={(e) => handleChange("isFeatured", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-all"
              >
                <option value="">All</option>
                <option value="true">Featured Only</option>
                <option value="false">Non-Featured</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Arrival
              </label>
              <select
                value={localFilters.isNewArrival}
                onChange={(e) => handleChange("isNewArrival", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-all"
              >
                <option value="">All</option>
                <option value="true">New Arrivals Only</option>
                <option value="false">Regular</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bestseller
              </label>
              <select
                value={localFilters.isBestseller}
                onChange={(e) => handleChange("isBestseller", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-all"
              >
                <option value="">All</option>
                <option value="true">Bestsellers Only</option>
                <option value="false">Regular</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stock Status
              </label>
              <select
                value={localFilters.inStock}
                onChange={(e) => handleChange("inStock", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-all"
              >
                <option value="">All</option>
                <option value="true">In Stock</option>
                <option value="false">Out of Stock</option>
              </select>
            </div>
          </>
        )}
      </div>

      {/* Manual Apply Button (Optional - only shown if autoApply is false) */}
      {!autoApply && (
        <div className="flex-shrink-0 p-4 border-t border-gray-200">
          <Button
            onClick={() => {
              onFilterChange(localFilters);
              if (onApply) onApply();
            }}
            variant="primary"
            className="w-full"
          >
            Apply Filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default FilterSidebar;
